import { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import * as ReactRouterDom from 'react-router-dom';
import {
  SuperTokensWrapper,
  getSuperTokensRoutesForReactRouterDom,
  EmailPasswordPreBuiltUI,
  ThirdPartyPreBuiltUI,
  PasswordlessPreBuiltUI,
  EmailVerificationPreBuiltUI,
  consumePendingCrossOriginAuthRedirect,
  useSessionContext,
  ProtectedRoute,
  Session,
} from '@om/shared-auth';
import { Layout } from '@/components/layout/Layout';
import { SmartDashboard } from '@/pages/SmartDashboard';
import { Profile } from '@/pages/Profile';
import { EventList } from '@/pages/EventList';
import { ManagedEvents } from '@/pages/ManagedEvents';
import { EventForm } from '@/pages/EventForm';
import { EventDetails } from '@/pages/EventDetails';
import { EventServicesRedirect } from '@/pages/EventServicesRedirect';
import { CelebrationList } from '@/pages/CelebrationList';
import { CelebrationDetails } from '@/pages/CelebrationDetails';
import { CelebrationGoalsPage } from '@/pages/CelebrationGoalsPage';
import { CelebrationForm } from '@/pages/CelebrationForm';
import { CelebrationNew } from '@/pages/CelebrationNew';
import { QuickEventCreate } from '@/pages/QuickEventCreate';
import { GuestManagement } from '@/pages/GuestManagement';
import { TaskManagement } from '@/pages/TaskManagement';
import { ServiceManagement } from '@/pages/ServiceManagement';
import { AddEventMember } from '@/pages/AddEventMember';
import { StreamChatPage } from '@/pages/StreamChatPage';
import { VenueList } from '@/pages/VenueList';
import { VenueDetail } from '@/pages/VenueDetail';
import { ContactsManagement } from '@/pages/ContactsManagement';
import { TeamMembersPage } from '@/pages/TeamMembersPage';
import { ActivityForm } from '@/pages/ActivityForm';
import { ActivityManagement } from '@/pages/ActivityManagement';
import { ActivityDetailsPage } from '@/pages/ActivityDetailsPage';
import { SubActivityFormPage } from '@/pages/SubActivityFormPage';
import { CelebrationGuestsGrid } from '@/pages/CelebrationGuestsGrid';
import SeatingSetup from '@/pages/SeatingSetup';
import { GuestLandingConfigPage } from '@/pages/GuestLandingConfigPage';
import { InvitationWorkspacePage } from '@/pages/InvitationWorkspacePage';
import { CelebrationTagsSetup } from '@/pages/CelebrationTagsSetup';
import { RegistryManagementPage } from '@/pages/RegistryManagementPage';
import { BlockerManagementPage } from '@/pages/BlockerManagementPage';
import { BookingManagementPage } from '@/pages/BookingManagementPage';
import { BookingLineItemsPage } from '@/pages/BookingLineItemsPage';
import { BookingDetailPage } from '@/pages/BookingDetailPage';
import { BudgetWorkspacePage } from '@/pages/BudgetWorkspacePage';
import { CelebrationQuoteComparison } from '@/pages/CelebrationQuoteComparison';
import { CelebrationQuoteDetail } from '@/pages/CelebrationQuoteDetail';
import { GoalDetailPage } from '@/pages/GoalDetailPage';
import EventDressCodePage from '@/pages/EventDressCodePage';
import { ServiceSelectionHub } from '@/pages/ServiceSelectionHub';
import { ServiceRequestNewPage, ServiceRequestUpdatePage } from '@/pages/ServiceRequestEditorPage';
import { MyServicesPage } from '@/pages/MyServicesPage';
import { ServiceTypeRequestsPage } from '@/pages/ServiceTypeRequestsPage';
import { ServiceRequestDetailPage } from '@/pages/ServiceRequestDetailPage';
import { ServiceRequestPostPage } from '@/pages/ServiceRequestPostPage';
import { ProposalDetailPage } from '@/pages/ProposalDetailPage';
import { CelebrationPromotionReadinessPage } from '@/pages/CelebrationPromotionReadinessPage';
import { LegacyCelebrationDashboardRedirect } from '@/components/navigation/LegacyCelebrationDashboardRedirect';

import { HowToPage } from '@/pages/HowToPage';
import { WebsiteFeedbackPage } from '@/pages/WebsiteFeedbackPage';
import { useTokenRefresh } from '@/hooks/useTokenRefresh';
import { ConversationProvider } from '@/contexts/ConversationContext';
import { NotificationsProvider } from '@/contexts/NotificationsContext';
import { AuthPageWrapper } from '@/components/auth/AuthPageWrapper';

const LandingPage = lazy(() => import('./pages/LandingPage'));

function LegacyInvitationEmailTemplateRedirect() {
  const { id } = ReactRouterDom.useParams<{ id: string }>();
  const location = ReactRouterDom.useLocation();
  const searchParams = new URLSearchParams(location.search);
  searchParams.set('tab', 'templates');
  return <Navigate to={`/celebrations/${id}/invitations?${searchParams.toString()}`} replace />;
}

function LegacyCelebrationQuotasRedirect() {
  const { id } = ReactRouterDom.useParams<{ id: string }>();
  return <Navigate to={`/celebrations/${id}/edit#guest-quotas`} replace />;
}

const renderLoadingScreen = (message: string) => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-center">
      <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      <p className="mt-2 text-sm text-gray-600">{message}</p>
    </div>
  </div>
);

const isOrganizerFallbackPath = (pathname: string): boolean => {
  return pathname === '/' || pathname === '/home' || pathname.startsWith('/home/');
};

function CrossAppRedirectGate({ children }: { children: React.ReactNode }) {
  const sessionContext = useSessionContext();
  const location = ReactRouterDom.useLocation();
  const [redirecting, setRedirecting] = useState(false);
  const doesSessionExist = sessionContext.loading ? false : sessionContext.doesSessionExist;

  useEffect(() => {
    if (redirecting || sessionContext.loading || !doesSessionExist) {
      return;
    }

    if (!isOrganizerFallbackPath(location.pathname)) {
      return;
    }

    const redirectTarget = consumePendingCrossOriginAuthRedirect();
    if (!redirectTarget) {
      return;
    }

    setRedirecting(true);
    window.location.replace(redirectTarget);
  }, [doesSessionExist, location.pathname, redirecting, sessionContext.loading]);

  if (redirecting) {
    return renderLoadingScreen('Returning to the requested admin page...');
  }

  return <>{children}</>;
}

// Inner component that uses hooks (must be inside SuperTokensWrapper)
function AppContent() {
  // These hooks must be called inside SuperTokensWrapper
  useTokenRefresh();
  const sessionContext = useSessionContext();
  const [swappingSession, setSwappingSession] = useState(false);

  // When the URL is a SuperTokens magic-link verify continuation route (contains
  // preAuthSessionId) and there is already an active session for a different user,
  // sign out the current session first so SuperTokens can consume the link for the
  // intended recipient instead of bouncing back as ALREADY_SIGNED_IN.
  useEffect(() => {
    if (sessionContext.loading || swappingSession) return;
    const hasSession = !sessionContext.loading && (sessionContext as any).doesSessionExist === true;
    if (!hasSession) return;

    const params = new URLSearchParams(window.location.search);
    const hasVerifyContinuation =
      params.has('preAuthSessionId') ||
      (window.location.pathname.startsWith('/auth') && window.location.hash.length > 1);

    if (!hasVerifyContinuation) return;

    setSwappingSession(true);
    Session.signOut().then(() => {
      // After sign-out, reload the page so SuperTokens processes the verify link fresh
      window.location.reload();
    });
  }, [sessionContext.loading, sessionContext, swappingSession]);

  if (swappingSession) {
    return renderLoadingScreen('Switching accounts...');
  }

  return (
    <ConversationProvider>
      <NotificationsProvider>
        <Router>
          <CrossAppRedirectGate>
            <div className="min-h-screen bg-background">
              <Routes>
                {getSuperTokensRoutesForReactRouterDom(ReactRouterDom, [
                  EmailPasswordPreBuiltUI,
                  ThirdPartyPreBuiltUI,
                  PasswordlessPreBuiltUI,
                  EmailVerificationPreBuiltUI,
                ]).map((route, i) => {
                  // Wrap each SuperTokens route with the branded auth page wrapper
                  const originalElement = route.props.element;
                  return (
                    <Route
                      key={`st-${i}`}
                      path={route.props.path}
                      element={<AuthPageWrapper>{originalElement}</AuthPageWrapper>}
                    />
                  );
                })}

          {/* Protected routes */}
          
          {/* Smart dashboard for initial loads - checks if celebrations exist and redirects */}
          <Route 
            path="/home" 
            element={
              <ProtectedRoute>
                <Layout>
                  <SmartDashboard />
                </Layout>
              </ProtectedRoute>
            } 
          />

          {/* Event routes */}
          <Route 
            path="/events" 
            element={
              <ProtectedRoute>
                <Layout>
                  <EventList />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/managed-events" 
            element={
              <ProtectedRoute>
                <Layout>
                  <ManagedEvents />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/events/new" 
            element={
              <ProtectedRoute>
                <Layout>
                  <EventForm mode="create" />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/celebrations/create"
            element={<Navigate to="/celebrations/new" replace />}
          />
          <Route
            path="/events/:id" 
            element={
              <ProtectedRoute>
                <Layout>
                  <EventDetails />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/events/:id/edit" 
            element={
              <ProtectedRoute>
                <Layout>
                  <EventForm mode="edit" />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/celebrations/:celebrationId/events/:eventId/dress-code" 
            element={
              <ProtectedRoute>
                <Layout>
                  <EventDressCodePage />
                </Layout>
              </ProtectedRoute>
            } 
          />

          {/* Venue routes */}
          <Route 
            path="/venues" 
            element={
              <ProtectedRoute>
                <Layout>
                  <VenueList />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/celebrations/:celebrationId/venues" 
            element={
              <ProtectedRoute>
                <Layout>
                  <VenueList />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/venues/:id" 
            element={
              <ProtectedRoute>
                <Layout>
                  <VenueDetail />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/celebrations/:celebrationId/venues/:id" 
            element={
              <ProtectedRoute>
                <Layout>
                  <VenueDetail />
                </Layout>
              </ProtectedRoute>
            } 
          />

          {/* Celebration routes */}
          <Route 
            path="/celebrations" 
            element={
              <ProtectedRoute>
                <Layout>
                  <CelebrationList />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/quick"
            element={
              <Layout>
                <QuickEventCreate />
              </Layout>
            }
          />
          <Route 
            path="/celebrations/new" 
            element={
              <Layout>
                <CelebrationNew />
              </Layout>
            } 
          />
          <Route 
            path="/celebrations/:id" 
            element={
              <ProtectedRoute>
                <Layout>
                  <CelebrationDetails />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/celebrations/:id/dashboard" 
            element={
              <ProtectedRoute>
                <LegacyCelebrationDashboardRedirect />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/celebrations/:id/promotion-readiness"
            element={
              <ProtectedRoute>
                <Layout>
                  <CelebrationPromotionReadinessPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route 
            path="/celebrations/:id/edit" 
            element={
              <ProtectedRoute>
                <Layout>
                  <CelebrationForm mode="edit" />
                </Layout>
              </ProtectedRoute>
            } 
          />

          {/* Celebration-specific management routes */}
          <Route 
            path="/celebrations/:id/setup" 
            element={
              <ProtectedRoute>
                <Layout>
                  <CelebrationTagsSetup />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/celebrations/:celebrationId/team" 
            element={
              <ProtectedRoute>
                <Layout>
                  <TeamMembersPage />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/celebrations/:celebrationId/events" 
            element={
              <ProtectedRoute>
                <Layout>
                  <EventList />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/celebrations/:celebrationId/events/new" 
            element={
              <ProtectedRoute>
                <Layout>
                  <EventForm mode="create" />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/celebrations/:celebrationId/guests" 
            element={
              <ProtectedRoute>
                <Layout>
                  <CelebrationGuestsGrid />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/celebrations/:id/tasks" 
            element={
              <ProtectedRoute>
                <Layout>
                  <TaskManagement />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route
            path="/celebrations/:id/goals"
            element={
              <ProtectedRoute>
                <Layout>
                  <CelebrationGoalsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route 
            path="/celebrations/:id/settings" 
            element={
              <ProtectedRoute>
                <Layout>
                  <CelebrationForm mode="edit" />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/celebrations/:id/guest-landing" 
            element={
              <ProtectedRoute>
                <Layout>
                  <GuestLandingConfigPage />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/celebrations/:id/invitation-email-template" 
            element={
              <ProtectedRoute>
                <LegacyInvitationEmailTemplateRedirect />
              </ProtectedRoute>
            }
          />
          <Route
            path="/celebrations/:id/email-templates"
            element={
              <ProtectedRoute>
                <LegacyInvitationEmailTemplateRedirect />
              </ProtectedRoute>
            }
          />
          <Route
            path="/celebrations/:id/guest-quotas"
            element={
              <ProtectedRoute>
                <LegacyCelebrationQuotasRedirect />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/celebrations/:id/registries" 
            element={
              <ProtectedRoute>
                <Layout>
                  <RegistryManagementPage />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route
            path="/celebrations/:celebrationId/invitations"
            element={
              <ProtectedRoute>
                <Layout>
                  <InvitationWorkspacePage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/celebrations/:celebrationId/blockers"
            element={
              <ProtectedRoute>
                <Layout>
                  <BlockerManagementPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/celebrations/:celebrationId/bookings"
            element={
              <ProtectedRoute>
                <Layout>
                  <BookingManagementPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/celebrations/:celebrationId/budget"
            element={
              <ProtectedRoute>
                <Layout>
                  <BudgetWorkspacePage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/celebrations/:celebrationId/bookings/:bookingId"
            element={
              <ProtectedRoute>
                <Layout>
                  <BookingDetailPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/celebrations/:celebrationId/bookings/:bookingId/line-items"
            element={
              <ProtectedRoute>
                <Layout>
                  <BookingLineItemsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/celebrations/:celebrationId/quotes"
            element={
              <ProtectedRoute>
                <Layout>
                  <CelebrationQuoteComparison />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/celebrations/:celebrationId/quotes/:quoteId"
            element={
              <ProtectedRoute>
                <Layout>
                  <CelebrationQuoteDetail />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/celebrations/:celebrationId/goals/:goalId"
            element={
              <ProtectedRoute>
                <Layout>
                  <GoalDetailPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/events/:eventId/goals/:goalId"
            element={
              <ProtectedRoute>
                <Layout>
                  <GoalDetailPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route 
            path="/events/:id/guests" 
            element={
              <ProtectedRoute>
                <Layout>
                  <GuestManagement />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/events/:id/tasks" 
            element={
              <ProtectedRoute>
                <Layout>
                  <TaskManagement />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/events/:id/add-member" 
            element={
              <ProtectedRoute>
                <Layout>
                  <AddEventMember />
                </Layout>
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/events/:id/services" 
            element={
              <ProtectedRoute>
                <Layout>
                  <EventServicesRedirect />
                </Layout>
              </ProtectedRoute>
            } 
          />

          {/* Seating Setup route */}
          <Route
            path="/events/:id/seating"
            element={
              <ProtectedRoute>
                <Layout>
                  <SeatingSetup />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Activity routes */}
          <Route 
            path="/events/:eventId/activities" 
            element={
              <ProtectedRoute>
                <Layout>
                  <ActivityManagement />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/events/:eventId/activities/new" 
            element={
              <ProtectedRoute>
                <Layout>
                  <ActivityForm />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/events/:eventId/activities/:id/edit" 
            element={
              <ProtectedRoute>
                <Layout>
                  <ActivityForm />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/activities/:id" 
            element={
              <ProtectedRoute>
                <Layout>
                  <ActivityDetailsPage />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/activities/:id/sub-activities/new" 
            element={
              <ProtectedRoute>
                <Layout>
                  <SubActivityFormPage />
                </Layout>
              </ProtectedRoute>
            } 
          />
          
          {/* Service selection routes (canonical) */}
          <Route
            path="/celebrations/:celebrationId/services/edit"
            element={
              <ProtectedRoute>
                <Layout>
                  <ServiceSelectionHub />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/celebrations/:celebrationId/services"
            element={
              <ProtectedRoute>
                <Layout>
                  <MyServicesPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Service selection routes (legacy aliases) */}
          <Route
            path="/celebrations/:celebrationId/services/select"
            element={
              <ProtectedRoute>
                <Layout>
                  <ServiceSelectionHub />
                </Layout>
              </ProtectedRoute>
            }
          />
          {/* Domain Redesign: New service request routes (W2-W5) */}
          <Route
            path="/celebrations/:celebrationId/services/:serviceId/requests/new"
            element={
              <ProtectedRoute>
                <Layout>
                  <ServiceRequestNewPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/celebrations/:celebrationId/services/:serviceId/requests/:requestId/edit"
            element={
              <ProtectedRoute>
                <Layout>
                  <ServiceRequestUpdatePage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/celebrations/:celebrationId/services/:serviceId/requests/:requestId/vendors/:vendorId"
            element={
              <ProtectedRoute>
                <Layout>
                  <ProposalDetailPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/celebrations/:celebrationId/services/:serviceId/requests/:requestId/vendors/:vendorId/proposal"
            element={
              <ProtectedRoute>
                <Layout>
                  <ProposalDetailPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/celebrations/:celebrationId/services/:serviceId/requests/:requestId/vendors/:vendorId/finalize"
            element={
              <ProtectedRoute>
                <Layout>
                  <ProposalDetailPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/celebrations/:celebrationId/services/:serviceId/requests/:requestId/post"
            element={
              <ProtectedRoute>
                <Layout>
                  <ServiceRequestPostPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/celebrations/:celebrationId/services/:serviceId/requests/:requestId"
            element={
              <ProtectedRoute>
                <Layout>
                  <ServiceRequestDetailPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/celebrations/:celebrationId/services/:serviceId"
            element={
              <ProtectedRoute>
                <Layout>
                  <ServiceTypeRequestsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          {/* Service management route */}
          <Route 
            path="/services" 
            element={
              <ProtectedRoute>
                <Layout>
                  <ServiceManagement />
                </Layout>
              </ProtectedRoute>
            } 
          />
          
          {/* Conversation route */}
          <Route 
            path="/conversation" 
            element={<Navigate to="/celebrations/new" replace />} 
          />
          
          {/* Celebration chat route */}
          <Route 
            path="/celebration-chat" 
            element={<Navigate to="/celebrations/new" replace />} 
          />

          {/* Chat page route - Stream Chat */}
          <Route 
            path="/chat" 
            element={
              <ProtectedRoute>
                <Layout>
                  <StreamChatPage />
                </Layout>
              </ProtectedRoute>
            } 
          />



          {/* Contacts management route */}
          <Route 
            path="/contacts" 
            element={
              <ProtectedRoute>
                <Layout>
                  <ContactsManagement />
                </Layout>
              </ProtectedRoute>
            } 
          />

          {/* Profile route */}
          <Route 
            path="/profile" 
            element={
              <ProtectedRoute>
                <Layout>
                  <Profile />
                </Layout>
              </ProtectedRoute>
            } 
          />

          {/* How To guide */}
          <Route 
            path="/how-to" 
            element={
              <ProtectedRoute>
                <Layout>
                  <HowToPage />
                </Layout>
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/feedback" 
            element={
              <ProtectedRoute>
                <Layout>
                  <WebsiteFeedbackPage />
                </Layout>
              </ProtectedRoute>
            } 
          />

          {/* Redirect root to appropriate page */}
          <Route 
            path="/" 
            element={<RootRedirect />}
          />
          
          {/* Catch all route */}
          <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </div>
          </CrossAppRedirectGate>
          </Router>
        </NotificationsProvider>
      </ConversationProvider>
  );
}

// Component that handles root route redirect (uses session context safely)
function RootRedirect() {
  const sessionContext = useSessionContext();

  if (sessionContext.loading) {
    return renderLoadingScreen('Loading...');
  }

  if (sessionContext.doesSessionExist) {
    return <Navigate to="/home" replace />;
  }

  // Show landing page for unauthenticated users
  return (
    <Suspense fallback={
      <div className="min-h-screen flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    }>
      <LandingPage />
    </Suspense>
  );
}

// Outer App component that wraps everything with SuperTokensWrapper
function App() {
  return (
    <SuperTokensWrapper>
      <AppContent />
    </SuperTokensWrapper>
  );
}

export default App;
